/**
 * Wall drag snaps (PRP-SKETCH-FIELD-4 K9, N9). While a wall is dragged it snaps, with
 * one haptic tick on the way in (the screen's), when its line comes within
 * `wall_snap_in` of:
 *   - `one_wall`: one stored wall thickness from a facing wall of another room (MCP-17 B-7564,
 *     founder rule 2026-11-02: neighbouring rooms share ONE wall). It reaches from `wall_snap_in`
 *     into the neighbour to `wall_snap_in` past one wall, so a drop flush or a little into the
 *     neighbour lands one wall away;
 *   - `corner`: a corner of another room near either end of the dragged wall (corner to corner);
 *   - `colinear`: the line of a parallel wall in another room;
 *   - `equal_size`: the position that makes this room's size across the dragged wall equal
 *     the matching size of a room it shares a wall with.
 * Two targets in range at once: the policy's `snap_priority` order wins, then the nearer.
 * Dragging past the reach releases it (the target is measured from where the finger is,
 * not from the snapped line). A guide line shows the snap.
 *
 * No target may leave the dragged wall less than one wall from a facing wall of another room
 * (onto its face, or between). Flush (0 in) would be right only for two rooms meant as one open
 * space, and the core has no such intent at drag time, so it never snaps there.
 */
import { POLICY, WALL_SNAP_FT, snapRank, type SnapKind } from './policy';
import { EPS, axisOf, bbox, edgeOf, findRoom, outN } from './geom';
import { wallFtOf } from './thickness';
import { sharedMap } from './shared';
import { findOpposite } from './opposite';
import type { Axis, Plan, Pt } from './types';

export interface WallSnap { kind: SnapKind; at: number; guide: [Pt, Pt] }

const lineAt = (ax: Axis, at: number, lo: number, hi: number): [Pt, Pt] => {
  const p = { x: 0, y: 0 }, q = { x: 0, y: 0 };
  const ox: Axis = ax === 'x' ? 'y' : 'x';
  p[ax] = q[ax] = at;
  p[ox] = lo;
  q[ox] = hi;
  return [p, q];
};

/**
 * The K9 target for wall `vid` of room `rid` (moving on axis `ax`) when the finger puts
 * its line at `raw`, or null. `r`'s corners are read where they are now (the wall's ends
 * along the other axis do not change during the drag).
 */
export function wallSnapTarget(plan: Plan, rid: string, vid: number, ax: Axis, raw: number): WallSnap | null {
  const r = findRoom(plan, rid);
  const E = r && edgeOf(r, vid);
  if (!r || !E) return null;
  const ox: Axis = ax === 'x' ? 'y' : 'x';
  const lo = Math.min(E.a[ox], E.b[ox]), hi = Math.max(E.a[ox], E.b[ox]);
  const t = axisOf(E.a, E.b);
  const s = Math.sign(outN(r, E.a, E.b)[ax]) || 1;
  const out: { kind: SnapKind; at: number; guide: [Pt, Pt]; inReach?: boolean }[] = [];
  const over = POLICY.snap_guide_overhang_ft;
  // Facing walls of other rooms that overlap the dragged wall along it: q's face at `b`, thickness `T`.
  const faces: { b: number; T: number }[] = [];
  for (const q of plan.rooms) {
    if (q === r) continue;
    const n = q.corners.length;
    // One wall: one stored thickness from a facing wall of q (its outward normal opposes ours).
    for (let i = 0; i < n; i++) {
      const a = q.corners[i], b = q.corners[(i + 1) % n];
      if (axisOf(a, b) !== t) continue;
      const l2 = Math.min(a[ox], b[ox]), h2 = Math.max(a[ox], b[ox]);
      if (Math.min(hi, h2) - Math.max(lo, l2) <= EPS) continue;
      const nq = outN(q, a, b);
      if (Math.sign(nq[ax]) !== -s) continue;
      const T = wallFtOf(q, a.id);
      faces.push({ b: a[ax], T });
      const at = a[ax] - s * T;
      const gap = (a[ax] - raw) * s; // the finger's gap to q's face
      out.push({
        kind: 'one_wall', at, inReach: gap >= -WALL_SNAP_FT - EPS && gap <= T + WALL_SNAP_FT + EPS, // policy-literal-ok: snap kind vocabulary
        guide: lineAt(ax, at, Math.min(lo, l2) - over, Math.max(hi, h2) + over),
      });
    }
    // Corner to corner: a corner of q near either end of the dragged wall.
    for (const c of q.corners) {
      const near = Math.min(Math.abs(c[ox] - lo), Math.abs(c[ox] - hi));
      if (near <= POLICY.snap_corner_reach_ft) {
        out.push({ kind: 'corner', at: c[ax], guide: lineAt(ax, c[ax], Math.min(lo, c[ox]) - over, Math.max(hi, c[ox]) + over) }); // policy-literal-ok: snap kind vocabulary
      }
    }
    // Colinear: the line of a parallel wall of q.
    for (let i = 0; i < n; i++) {
      const a = q.corners[i], b = q.corners[(i + 1) % n];
      if (axisOf(a, b) !== t) continue;
      const l2 = Math.min(a[ox], b[ox]), h2 = Math.max(a[ox], b[ox]);
      out.push({ kind: 'colinear', at: a[ax], guide: lineAt(ax, a[ax], Math.min(lo, l2) - over, Math.max(hi, h2) + over) }); // policy-literal-ok: snap kind vocabulary
    }
  }
  // Equal size: this room's size across the wall equal to a neighbour's matching size.
  const opp = findOpposite(r, vid);
  if (opp) {
    const sign = Math.sign(E.a[ax] - opp.a[ax]) || 1;
    const neighbours = new Set<string>();
    sharedMap(plan).get(r.id)?.forEach((ivs) => ivs.forEach((iv) => neighbours.add(iv.orid)));
    for (const id of neighbours) {
      const q = findRoom(plan, id)!;
      const [x0, y0, x1, y1] = bbox(q.corners);
      const size = ax === 'x' ? x1 - x0 : y1 - y0;
      const at = opp.a[ax] + sign * size;
      out.push({ kind: 'equal_size', at, guide: lineAt(ax, at, lo - over, hi + over) }); // policy-literal-ok: snap kind vocabulary
    }
  }
  // Never less than one wall from a facing wall (onto its face or between).
  const tooClose = (at: number) => faces.some((f) => {
    const gap = (f.b - at) * s;
    return gap > -f.T - EPS && gap < f.T - EPS;
  });
  let best: (typeof out)[number] | null = null;
  for (const c of out) {
    const d = Math.abs(c.at - raw);
    if (c.inReach !== undefined ? !c.inReach : d > WALL_SNAP_FT + EPS) continue;
    if (c.kind !== 'one_wall' && tooClose(c.at)) continue; // policy-literal-ok: snap kind vocabulary
    if (!best || snapRank(c.kind) < snapRank(best.kind)
      || (snapRank(c.kind) === snapRank(best.kind) && d < Math.abs(best.at - raw))) best = c;
  }
  return best ? { kind: best.kind, at: best.at, guide: best.guide } : null;
}
